<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'dialogCharacter',
})
</script>
<template>
  <ant-Modal v-model:open="state.dialog.visible" :centered="true" :wrap-class-name="'wrap-character-dialog'"
    :maskClosable="false" :closable="false" :keyboard="false" :title="state.dialog.title" :width="state.dialog.width"
    :destroyOnClose="true" :confirm-loading="state.loading.onSubmit" :ok-text="state.dialog.submitTxt" @ok="onSubmit"
    @cancel="closeDialog">
    <div class="px-[36px] py-[20px]">
      <a-spin :spinning="state.loading.dialogUpload">
        <a-form ref="formRef" :model="state.ruleForm" :rules="state.rules" :wrapper-col="{ style: 'flex: 1' }">
          <a-form-item label="角色名称" name="name">
            <a-input showCount v-model:value="state.ruleForm.name" placeholder="请输入角色名称" :maxlength="20" allow-clear />
          </a-form-item>
          <a-form-item label="数据范围" name="permType">
            <a-radio-group v-model:value="state.ruleForm.permType" :disabled="state.viewType === 'edit'">
              <a-radio value="1">全部</a-radio>
            </a-radio-group>
          </a-form-item>
          <a-form-item name="menuIds" :required="true" label="功能范围">
            <div class="character-area">
              <div class="character-area-tabs">
                <a-radio-group v-model:value="areaType" optionType="button">
                  <a-radio-button type="text" value="1">后台权限</a-radio-button>
                  <a-radio-button type="text" value="2">小程序权限</a-radio-button>
                </a-radio-group>
              </div>
              <div v-show="areaType == '1'" class="character-area-tree">
                <a-tree :checked-keys="checkedKeysPc" :checkable="true" :tree-data="treeData"
                  :field-names="{ title: 'title', key: 'id', children: 'children' }" :check-strictly="true"
                  @check="handleTreeCheck">
                  <template #title="{ dataRef }">
                    <span v-if="dataRef.resourceType === '1'">{{ t(dataRef.title) }}</span>
                    <span v-else @mousedown.stop @click.stop>
                      <a-switch v-model:checked="dataRef.value" size="small" @change="handleSwitchChange(dataRef)" />
                      <span class="ml5">{{ t(dataRef.title) }}</span>
                    </span>
                  </template>
                </a-tree>
              </div>
              <div v-show="areaType == '2'" class="character-area-tree">
                <a-tree :checked-keys="checkedKeysMini" :checkable="true" :tree-data="treeMiniData"
                  :field-names="{ title: 'title', key: 'id', children: 'children' }" :check-strictly="true"
                  @check="handleTreeCheck">
                  <template #title="{ dataRef }">
                    <span v-if="dataRef.resourceType === '1'">{{ t(dataRef.title) }}</span>
                    <span v-else @mousedown.stop @click.stop>
                      <a-switch v-model:checked="dataRef.value" size="small" @change="handleSwitchChange(dataRef)" />
                      <span class="ml5">{{ t(dataRef.title) }}</span>
                    </span>
                  </template>
                </a-tree>
              </div>
            </div>
          </a-form-item>
        </a-form>
      </a-spin>
    </div>
  </ant-Modal>
</template>

<script setup lang="ts" name="dialogCharacter">
import { reactive, ref } from 'vue'

import { cloneDeep } from 'lodash-es'
import { useCharacter } from '@/store/system/character'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const characterStore = useCharacter()

const emit = defineEmits(['createUpdate'])

const formRef = ref()
const treeData = ref<any[]>([])
const treeMiniData = ref<any[]>([])
const areaType = ref('1')
const checkedKeysPc = ref<string[]>([])
const checkedKeysMini = ref<string[]>([])

const INIT_RULE_FORM = {
  id: '',
  name: '',
  permType: '1',
  areaType: '1',
  areaIds: [],
  menuIds: [] as string[]
}

const state = reactive({
  dialog: {
    visible: false,
    title: '',
    submitTxt: '',
    width: '645px'
  },
  loading: {
    dialogUpload: false,
    onSubmit: false
  },
  ruleForm: cloneDeep(INIT_RULE_FORM),
  rules: {
    name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
    permType: [{ required: true, message: '请选择数据范围', trigger: 'change' }],
    menuIds: [{ required: true, message: '请至少选择一项功能权限', type: 'array' }]
  },
  viewType: ''
})

const normalizeCheckedKeys = (checkedKeys: any): string[] => {
  if (Array.isArray(checkedKeys)) {
    return checkedKeys.map(String)
  }
  if (checkedKeys?.checked && Array.isArray(checkedKeys.checked)) {
    return checkedKeys.checked.map(String)
  }
  return []
}

const extractLeafIds = (tree: any[]): string[] => {
  const ids: string[] = []
  const walk = (nodes: any[]) => {
    if (!Array.isArray(nodes)) return
    for (const node of nodes) {
      if (!node.children?.length) {
        ids.push(String(node.id))
      } else {
        walk(node.children)
      }
    }
  }
  walk(tree)
  return ids
}

const getCheckedKeysWithParents = (checkedKeys: any, tree: any[]): string[] => {
  const checkedIdSet = new Set(normalizeCheckedKeys(checkedKeys))
  const result = new Set<string>()

  const walk = (nodes: any[], parentIds: string[] = []) => {
    if (!Array.isArray(nodes)) return

    for (const node of nodes) {
      const currentId = String(node.id)
      const currentParentIds = [...parentIds]

      if (checkedIdSet.has(currentId)) {
        currentParentIds.forEach((id) => result.add(id))
        result.add(currentId)
      }

      if (node.children?.length) {
        walk(node.children, [...currentParentIds, currentId])
      }
    }
  }

  walk(tree)
  return Array.from(result)
}

const getParentIds = (targetId: string, tree: any[]): string[] => {
  let parentIds: string[] = []

  const walk = (nodes: any[], parents: string[] = []): boolean => {
    if (!Array.isArray(nodes)) return false

    for (const node of nodes) {
      const currentId = String(node.id)
      if (currentId === targetId) {
        parentIds = parents
        return true
      }

      if (node.children?.length && walk(node.children, [...parents, currentId])) {
        return true
      }
    }

    return false
  }

  walk(tree)
  return parentIds
}

const getDescendantIds = (node: any): string[] => {
  const ids: string[] = []

  const walk = (nodes: any[]) => {
    if (!Array.isArray(nodes)) return

    for (const child of nodes) {
      ids.push(String(child.id))
      if (child.children?.length) {
        walk(child.children)
      }
    }
  }

  walk(node?.children || [])
  return ids
}

const getTreeContext = (type: string) => {
  return type === '2'
    ? { checkedKeys: checkedKeysMini, tree: treeMiniData }
    : { checkedKeys: checkedKeysPc, tree: treeData }
}

const syncCheckedKeys = (keys: string[], type = areaType.value) => {
  const { checkedKeys } = getTreeContext(type)
  checkedKeys.value = Array.from(new Set(keys.map(String)))
  state.ruleForm.menuIds = cloneDeep(Array.from(new Set([...checkedKeysPc.value, ...checkedKeysMini.value])))
  syncSwitchWithTree(type)
}

const filterMenuTree = (nodes: any[]): any[] => {
  if (!Array.isArray(nodes)) return []
  return nodes
    .filter((item: any) => item.platformType != '2' && item.isIframe != '1')
    .map((item: any) => ({
      ...item,
      id: String(item.id),
      checkable: item.resourceType === '1',
      children: filterMenuTree(item.children || [])
    }))
}

const filterMenuMiniAppTree = (nodes: any[]): any[] => {
  if (!Array.isArray(nodes)) return []
  return nodes
    .filter((item: any) => item.platformType == '2' && item.isIframe != '1')
    .map((item: any) => ({
      ...item,
      id: String(item.id),
      checkable: item.resourceType === '1',
      children: filterMenuMiniAppTree(item.children || [])
    }))
}

const initMenuTree = async () => {
  const list: any = await characterStore.getAllTreeMenu()
  const data = Array.isArray(list) ? list : []
  treeData.value = filterMenuTree(data)
  treeMiniData.value = filterMenuMiniAppTree(data)
}

const openDialog = async (type: string, row?: any) => {
  state.dialog.visible = true
  state.loading.dialogUpload = true
  state.viewType = type

  await initMenuTree()

  if (type === 'edit' && row) {
    const selectedPcIds = extractLeafIds(
      row.ucMenuList?.filter((i: any) => i.platformType != '2' && i.isIframe != '1') || []
    )
    const selectedMiniIds = extractLeafIds(
      row.ucMenuList?.filter((i: any) => i.platformType == '2' && i.isIframe != '1') || []
    )
    state.ruleForm = {
      ...INIT_RULE_FORM,
      id: row.id,
      name: row.name,
      permType: row.permType
    }

    syncCheckedKeys(getCheckedKeysWithParents(selectedPcIds, treeData.value), '1')
    syncCheckedKeys(getCheckedKeysWithParents(selectedMiniIds, treeMiniData.value), '2')

    state.dialog.title = '修改角色'
    state.dialog.submitTxt = '修改'
  } else {
    state.ruleForm = cloneDeep(INIT_RULE_FORM)
    syncCheckedKeys([], '1')
    syncCheckedKeys([], '2')
    state.dialog.title = '新增角色'
    state.dialog.submitTxt = '确认'
  }

  state.loading.dialogUpload = false
}

const closeDialog = () => {
  state.dialog.visible = false
  state.loading.onSubmit = false
  state.ruleForm = cloneDeep(INIT_RULE_FORM)
  checkedKeysPc.value = []
  checkedKeysMini.value = []
  areaType.value = '1'
  formRef.value?.resetFields()
}

const onSubmit = () => {
  formRef.value
    .validate()
    .then(async () => {
      state.loading.onSubmit = true
      const menuIds = Array.from(new Set([
        ...getCheckedKeysWithParents(checkedKeysPc.value, treeData.value),
        ...getCheckedKeysWithParents(checkedKeysMini.value, treeMiniData.value)
      ]))
      menuIds.unshift('-1')

      const param = {
        ...state.ruleForm,
        menuIds
      }

      try {
        await characterStore.addOrEditRole(param)
        state.loading.onSubmit = false
        emit('createUpdate')
      } catch {
        state.loading.onSubmit = false
      }
    })
    .catch(() => {
      state.loading.onSubmit = false
    })
}

const syncSwitchWithTree = (type = areaType.value) => {
  const { checkedKeys, tree } = getTreeContext(type)
  const allChecked = [...checkedKeys.value]
  const walk = (nodes: any[]) => {
    for (const node of nodes) {
      if (node.resourceType !== '1') {
        node.value = allChecked.includes(String(node.id))
      }
      if (node.children?.length) {
        walk(node.children)
      }
    }
  }
  walk(tree.value)
}

const handleSwitchChange = (data: any) => {
  const type = areaType.value
  const { checkedKeys, tree } = getTreeContext(type)
  const currentId = String(data.id)
  const keys = data.value
    ? [...checkedKeys.value, ...getParentIds(currentId, tree.value), currentId]
    : checkedKeys.value.filter((key) => String(key) !== currentId)

  syncCheckedKeys(keys, type)
}

const handleTreeCheck = (_checkedKeys: any, info: any) => {
  const type = areaType.value
  const { checkedKeys, tree } = getTreeContext(type)
  const node = info.node?.dataRef || info.node
  const currentId = String(node.id ?? node.key ?? node.eventKey)
  const currentKeys = checkedKeys.value.map(String)
  let keys: string[]

  if (info.checked) {
    keys = [
      ...currentKeys,
      ...getParentIds(currentId, tree.value),
      currentId,
      ...getDescendantIds(node)
    ]
  } else {
    const removeIds = new Set([currentId, ...getDescendantIds(node)])
    keys = currentKeys.filter((key) => !removeIds.has(String(key)))
  }

  syncCheckedKeys(keys, type)
}



defineExpose({
  openDialog,
  closeDialog
})
</script>

<style scoped lang="less">
:deep(.ant-form-item) {
  margin-bottom: 0px;
  padding-bottom: 24px;
}

:deep(.ant-form-item-label) {
  margin-left: 0px;
  margin-right: 5px;
  width: 80px;
  text-align: right;
  justify-content: flex-end;
  display: flex;
  align-items: center;
  height: 40px;
}

:deep(.ant-form-item-label > label) {
  font-size: 14px;
  line-height: 1;
  font-weight: 400;
}

:deep(.ant-input-affix-wrapper) {
  height: 40px;
  padding: 0 11px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

:deep(.ant-input-disabled) {
  height: 40px;
  border-color: #C5C5D3;
  border-radius: 4px;
}

:deep(.ant-select-selector) {
  height: 40px !important;
  display: flex;
  align-items: center;
  border-radius: 4px;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-input-affix-wrapper:not(:hover):not(.ant-input-affix-wrapper-focused)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-select:not(.ant-select-focused) .ant-select-selector:not(:hover)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(textarea.ant-input:not(:hover):not(:focus)) {
  border-color: #C5C5D3;
}

:deep(.ant-radio-wrapper) {
  height: 40px;
  display: flex;
  align-items: center;
}


.character-area {
  width: 100%;
  border: 1px solid #C5C5D3;
  border-radius: 4px;

  .character-area-tabs {
    height: 38px;
    display: flex;
    align-items: center;
    border-bottom: none;
    padding: 4px 12px;
    font-weight: 400;
    background: #F1F1F1;
  }

  .character-area-tree {
    padding: 10px;

    :deep(.ant-tree-title) {
      font-size: 14px;
      font-weight: 400;
    }
  }
}

.ml5 {
  margin-left: 5px;
}
</style>
